/**
 * F-024 — Merge particle burst.
 *
 * Spawns a short-lived (~700ms) Points cloud at the merge point, color
 * tinted by the resulting tier. Particles spread outward from origin
 * with random radial velocity then fade. Lightweight — created and
 * destroyed each merge; if perf becomes an issue F-035 will pool.
 */

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  Points,
  PointsMaterial,
  type Object3D,
  type Scene,
} from "three";

const PARTICLE_COUNT = 40;
const LIFETIME_MS = 700;
const SPREAD_RADIUS = 1.5;
const PARTICLE_SIZE = 0.18;

// Spark burst — fast, bright, short-lived. Used for collision impact
// FX (any tier-vs-tier or meteor-vs-Earth contact).
const SPARK_COUNT = 14;
const SPARK_LIFETIME_MS = 320;
const SPARK_SPREAD = 2.6;
const SPARK_SIZE = 0.30;

interface ActiveBurst {
  points: Points;
  velocities: Float32Array;
  spawnedAt: number;
  origin: { x: number; y: number; z: number };
  material: PointsMaterial;
  lifetimeMs: number;
}

export interface SparkOptions {
  position: { x: number; y: number; z: number };
  color: number;
  /** Multiplier for spread/size. Default 1; bigger collisions = bigger sparks. */
  intensity?: number;
}

export interface ParticleBurstSystem {
  /** Spawn a burst at world position, color tinted to the tier hex. */
  spawn(opts: { position: { x: number; y: number; z: number }; color: number }): void;
  /** Spawn a short-lived spark burst — collision impact FX. */
  spawnSpark(opts: SparkOptions): void;
  /** Tick to advance/cull. Pass simulation time in ms. */
  tick(nowMs: number): void;
  /** Active bursts count (handy for tests / debug). */
  activeCount(): number;
  /** Clear all active bursts (used on restart). */
  clear(): void;
  /** Detach scene root + dispose all geometries/materials. */
  dispose(): void;
}

export interface ParticleBurstOptions {
  scene: Scene;
}

export function createParticleBurstSystem(
  options: ParticleBurstOptions,
): ParticleBurstSystem {
  const root: Object3D = options.scene;
  const active: ActiveBurst[] = [];

  const spawn = ({ position, color }: { position: { x: number; y: number; z: number }; color: number }): void => {
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    const velocities = new Float32Array(PARTICLE_COUNT * 3);
    for (let i = 0; i < PARTICLE_COUNT; i += 1) {
      const off = i * 3;
      positions[off] = position.x;
      positions[off + 1] = position.y;
      positions[off + 2] = position.z;
      // Random outward velocity in a sphere
      const phi = Math.random() * Math.PI * 2;
      const theta = Math.acos(Math.random() * 2 - 1);
      const speed = Math.random() * SPREAD_RADIUS;
      velocities[off] = Math.sin(theta) * Math.cos(phi) * speed;
      velocities[off + 1] = Math.sin(theta) * Math.sin(phi) * speed;
      velocities[off + 2] = Math.cos(theta) * speed;
    }
    const geometry = new BufferGeometry();
    geometry.setAttribute("position", new BufferAttribute(positions, 3));
    const material = new PointsMaterial({
      color: new Color(color),
      size: PARTICLE_SIZE,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    const points = new Points(geometry, material);
    points.name = "merge-burst";
    root.add(points);
    active.push({
      points,
      velocities,
      spawnedAt: nowOrZero(),
      origin: { ...position },
      material,
      lifetimeMs: LIFETIME_MS,
    });
  };

  const spawnSpark = ({ position, color, intensity = 1 }: SparkOptions): void => {
    const spread = SPARK_SPREAD * intensity;
    const positions = new Float32Array(SPARK_COUNT * 3);
    const velocities = new Float32Array(SPARK_COUNT * 3);
    for (let i = 0; i < SPARK_COUNT; i += 1) {
      const off = i * 3;
      positions[off] = position.x;
      positions[off + 1] = position.y;
      positions[off + 2] = position.z;
      // Camera-facing 2D burst so sparks read as drawn shapes against
      // the play plane rather than spraying into depth.
      const angle = (i / SPARK_COUNT) * Math.PI * 2 + Math.random() * 0.4;
      const speed = spread * (0.6 + Math.random() * 0.6);
      velocities[off] = Math.cos(angle) * speed;
      velocities[off + 1] = Math.sin(angle) * speed;
      velocities[off + 2] = 0;
    }
    const geometry = new BufferGeometry();
    geometry.setAttribute("position", new BufferAttribute(positions, 3));
    const material = new PointsMaterial({
      color: new Color(color),
      size: SPARK_SIZE * (0.85 + intensity * 0.4),
      transparent: true,
      opacity: 1,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    const points = new Points(geometry, material);
    points.name = "impact-spark";
    root.add(points);
    active.push({
      points,
      velocities,
      spawnedAt: nowOrZero(),
      origin: { ...position },
      material,
      lifetimeMs: SPARK_LIFETIME_MS,
    });
  };

  const removeAt = (idx: number): void => {
    const burst = active[idx];
    if (!burst) return;
    root.remove(burst.points);
    burst.points.geometry.dispose();
    burst.material.dispose();
    active.splice(idx, 1);
  };

  const tick = (nowMs: number): void => {
    for (let i = active.length - 1; i >= 0; i -= 1) {
      const burst = active[i]!;
      const elapsed = nowMs - burst.spawnedAt;
      const t = Math.min(elapsed / burst.lifetimeMs, 1);
      const positions = burst.points.geometry.getAttribute("position") as BufferAttribute;
      const arr = positions.array as Float32Array;
      const particleCount = arr.length / 3;
      for (let p = 0; p < particleCount; p += 1) {
        const off = p * 3;
        arr[off] = burst.origin.x + burst.velocities[off]! * t;
        arr[off + 1] = burst.origin.y + burst.velocities[off + 1]! * t;
        arr[off + 2] = burst.origin.z + burst.velocities[off + 2]! * t;
      }
      positions.needsUpdate = true;
      burst.material.opacity = 1 - t;
      if (elapsed >= burst.lifetimeMs) {
        removeAt(i);
      }
    }
  };

  const activeCount = (): number => active.length;

  const clear = (): void => {
    while (active.length > 0) removeAt(active.length - 1);
  };

  const dispose = clear;

  return { spawn, spawnSpark, tick, activeCount, clear, dispose };
}

function nowOrZero(): number {
  return typeof performance !== "undefined" ? performance.now() : 0;
}

export const __testing = { PARTICLE_COUNT, LIFETIME_MS, SPARK_COUNT, SPARK_LIFETIME_MS };
